import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collections, upcomingEvent, NOTIFY_ENDPOINT } from "../data/collections";
import { demoProducts } from "../data/demoProducts";
import ImageWithPlaceholder from "../components/common/ImageWithPlaceholder";
import Footer from "../components/Home/Footer/Footer";
import { API_URL, PRODUCTS_URL } from "../config";
import { useSiteAssets, optimized } from "../siteAssets";
import styles from "./collections.module.css";

// A product belongs to a collection when its linked collection (API), demo
// collection string, or brand matches the collection's shopKey.
const collectionKey = (p) =>
  p.productCollection?.name ||
  (typeof p.collection === "string" ? p.collection : "") ||
  p.brand ||
  "";

// Case-insensitive: backoffice names may be styled ALL-CAPS
const sameName = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const pad = (n) => String(n).padStart(2, "0");

const Countdown = ({ target }) => {
  const getLeft = () => Math.max(0, new Date(target).getTime() - Date.now());
  const [left, setLeft] = useState(getLeft);

  useEffect(() => {
    const id = setInterval(() => setLeft(getLeft()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (left <= 0) return <p className={styles.dropped}>Happening now</p>;

  const cells = [
    [Math.floor(left / 86400000), "Days"],
    [Math.floor((left % 86400000) / 3600000), "Hrs"],
    [Math.floor((left % 3600000) / 60000), "Min"],
    [Math.floor((left % 60000) / 1000), "Sec"],
  ];

  return (
    <div className={styles.countdown} aria-label="Time until event">
      {cells.map(([value, label]) => (
        <div className={styles.count} key={label}>
          <span className={styles.countNum}>{pad(value)}</span>
          <span className={styles.countLbl}>{label}</span>
        </div>
      ))}
    </div>
  );
};

const RegisterForm = ({ context }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      // Register in the store database (sends the welcome email server-side)
      const res = await fetch(`${API_URL}/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "collections" }),
      });
      // Backup ping to the store inbox
      fetch(NOTIFY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, _subject: `Registration: ${context}`, context }),
      }).catch(() => {});
      setStatus(res.ok ? "done" : "error");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <p className={styles.notifyDone}>
        You’re registered — we’ll email you the details shortly.
      </p>
    );
  }

  return (
    <form className={styles.notify} onSubmit={handleSubmit}>
      <input
        type="email"
        required
        className={styles.notifyInput}
        placeholder="Enter your email"
        aria-label="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button type="submit" className={styles.notifyBtn} disabled={status === "sending"}>
        {status === "sending" ? "Registering…" : "Register"}
      </button>
      {status === "error" && (
        <span className={styles.notifyErr}>Something went wrong — please try again.</span>
      )}
    </form>
  );
};

const Collections = () => {
  const [products, setProducts] = useState(demoProducts);
  const assets = useSiteAssets();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(PRODUCTS_URL);
        const data = await response.json();
        if (response.ok && Array.isArray(data.products) && data.products.length) {
          setProducts(data.products);
        }
      } catch (error) {
        // API unreachable — keep the demo fallback
        console.error("Failed to fetch products:", error);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.intro}>
        <p className={styles.introEyebrow}>Collections</p>
        <h1 className={styles.introTitle}>Shop by Collection</h1>
        <p className={styles.introText}>
          Every Lormami collection tells a story. Explore the worlds below and
          shop the pieces.
        </p>
      </header>

      {collections.map((collection) => {
        const looks = products
          .filter((p) => sameName(collectionKey(p), collection.shopKey))
          .slice(0, 4);
        return (
          <section className={styles.collection} key={collection.id}>
            <div
              className={styles.banner}
              style={{
                backgroundImage: `linear-gradient(to top, rgba(15,13,11,0.82), rgba(15,13,11,0.28)), url(${
                  assets[`collection-${collection.id}`]
                    ? optimized(assets[`collection-${collection.id}`])
                    : collection.heroImage
                })`,
              }}
            >
              <div className={styles.bannerInner}>
                <span className={styles.chip}>{collection.season}</span>
                <h2 className={styles.name}>{collection.name}</h2>
                <p className={styles.tagline}>{collection.tagline}</p>
                <p className={styles.story}>{collection.story}</p>
                <Link
                  to={`/shop?collection=${encodeURIComponent(collection.shopKey)}`}
                  className={styles.shopBtn}
                >
                  Shop the Collection
                </Link>
              </div>
            </div>

            {looks.length > 0 && (
              <>
                <div className={styles.looksHead}>
                  <span className={styles.looksTitle}>Featured Pieces</span>
                  <Link
                    to={`/shop?collection=${encodeURIComponent(collection.shopKey)}`}
                    className={styles.looksCount}
                  >
                    View all →
                  </Link>
                </div>
                <div className={styles.looks}>
                  {looks.map((look) => (
                    <article className={styles.look} key={look.id}>
                      <div className={styles.lookFrame}>
                        {look.outOfStock && (
                          <span className={styles.lookTag}>Sold Out</span>
                        )}
                        <ImageWithPlaceholder
                          src={look.image}
                          alt={look.name}
                          className={styles.lookImg}
                          wrapperStyle={{ position: "absolute", inset: 0, display: "block" }}
                        />
                      </div>
                      <h3 className={styles.lookName}>{look.name}</h3>
                      <p className={styles.lookTeaser}>
                        ₦ {new Intl.NumberFormat("en-NG").format(look.price)}
                      </p>
                    </article>
                  ))}
                </div>
              </>
            )}
            <hr className={styles.divider} />
          </section>
        );
      })}

      {/* Upcoming event registration */}
      <section className={styles.collection}>
        <div
          className={styles.banner}
          style={{
            backgroundImage: `linear-gradient(to top, rgba(15,13,11,0.85), rgba(15,13,11,0.4)), url(${
              assets.event ? optimized(assets.event) : upcomingEvent.image
            })`,
          }}
        >
          <div className={styles.bannerInner}>
            <span className={styles.chip}>
              <span className={styles.chipDot} />
              {upcomingEvent.date
                ? `Upcoming Event · ${upcomingEvent.location}`
                : "The LORMAMI Community"}
            </span>
            <h2 className={styles.name}>{upcomingEvent.title}</h2>
            <p className={styles.tagline}>{upcomingEvent.blurb}</p>
            {upcomingEvent.date && <Countdown target={upcomingEvent.date} />}
            <RegisterForm context={upcomingEvent.title} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Collections;
